import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Filter, X, Plus, Ban, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog to edit a client's market filters used in Opportunities scoring.
 * - excluded_terms : keywords containing any of these terms are HIDDEN from opportunities
 *                    (e.g. "isolation" for an "aménagement"-only client)
 * - focus_terms    : keywords containing any of these terms are BOOSTED ×1.5
 *                    (e.g. "aménagement", "extension", "rénovation")
 */
export default function MarchesDialog({ open, onOpenChange, client, onSaved }) {
  const [excluded, setExcluded] = useState([]);
  const [focus, setFocus] = useState([]);
  const [excludedInput, setExcludedInput] = useState('');
  const [focusInput, setFocusInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setExcluded(client.excluded_terms || []);
      setFocus(client.focus_terms || []);
    }
  }, [client]);

  const addTerm = (list, setList, value, setInput) => {
    const trimmed = (value || '').trim().toLowerCase();
    if (!trimmed || list.includes(trimmed)) return;
    if (list.length >= 50) {
      toast.error('Maximum 50 termes');
      return;
    }
    setList([...list, trimmed]);
    setInput('');
  };

  const removeTerm = (list, setList, term) => setList(list.filter(t => t !== term));

  const handleSave = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/clients/${client.id}/stats-settings`,
        { excluded_terms: excluded, focus_terms: focus },
        { withCredentials: true }
      );
      toast.success(
        `${client.name} : ${excluded.length} exclu${excluded.length > 1 ? 's' : ''}, ${focus.length} focus`
      );
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, list, setList, value, setInput) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTerm(list, setList, value, setInput);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#002FA7]" />
            Marchés exclus & Focus — {client?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Comment cela fonctionne ?</p>
            <ul className="text-xs mt-1 space-y-1 ml-4 list-disc">
              <li><strong>Marchés exclus</strong> : tout mot-clé contenant un de ces termes est <strong>masqué</strong> des opportunités (ex : exclure "isolation" pour un client qui ne fait que de l'aménagement).</li>
              <li><strong>Focus</strong> : tout mot-clé contenant un de ces termes voit son score <strong>boosté ×1.5</strong> (ex : focus sur "aménagement", "extension", "rénovation").</li>
              <li>La recherche est insensible à la casse et fonctionne en sous-chaîne (ex : "isolation" matche "isolation thermique").</li>
            </ul>
          </div>

          {/* Excluded terms */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-600" />
              <Label className="text-sm font-medium">Marchés exclus ({excluded.length})</Label>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={excludedInput}
                onChange={(e) => setExcludedInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, excluded, setExcluded, excludedInput, setExcludedInput)}
                placeholder="Ex : isolation, peinture, toiture..."
                className="flex-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                data-testid="excluded-term-input"
              />
              <Button
                onClick={() => addTerm(excluded, setExcluded, excludedInput, setExcludedInput)}
                variant="outline"
                size="sm"
                disabled={!excludedInput.trim()}
                data-testid="add-excluded-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {excluded.length === 0 ? (
                <span className="text-xs text-neutral-400 italic">Aucun marché exclu — tous les mots-clés du domaine seront analysés.</span>
              ) : (
                excluded.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">
                    {t}
                    <button
                      onClick={() => removeTerm(excluded, setExcluded, t)}
                      className="hover:text-red-900"
                      data-testid={`remove-excluded-${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Focus terms */}
          <div className="space-y-2 pt-3 border-t border-neutral-200">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-600" />
              <Label className="text-sm font-medium">Focus ({focus.length})</Label>
              <span className="text-[11px] text-neutral-500">— score ×1.5 si match</span>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={focusInput}
                onChange={(e) => setFocusInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, focus, setFocus, focusInput, setFocusInput)}
                placeholder="Ex : aménagement, extension, rénovation..."
                className="flex-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                data-testid="focus-term-input"
              />
              <Button
                onClick={() => addTerm(focus, setFocus, focusInput, setFocusInput)}
                variant="outline"
                size="sm"
                disabled={!focusInput.trim()}
                data-testid="add-focus-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {focus.length === 0 ? (
                <span className="text-xs text-neutral-400 italic">Aucun terme focus — tous les marchés sont traités à poids égal.</span>
              ) : (
                focus.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    {t}
                    <button
                      onClick={() => removeTerm(focus, setFocus, t)}
                      className="hover:text-amber-900"
                      data-testid={`remove-focus-${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Annuler
            </Button>
            <Button
              className="bg-[#002FA7] hover:bg-[#002277]"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-marches-btn"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
